<template>
	<div>
		<div class="d-flex flex-row align-center justify-space-between my-2 mb-0 px-4">
			<p class="mb-0 font-weight-bold body-2" style="color: #524D4D">Porcentajes de Patrocinio</p>
			<v-tooltip bottom>
				<template v-slot:activator="{ on, attrs }">
					<v-btn icon color="grey" class="mr-3"
						v-bind="attrs"
						v-on="on"
						small
						v-if="$checkPermissions(['create_company_supervision_percents'])"
						@click="openModal(null)">
						<v-icon>mdi-plus</v-icon>
					</v-btn>
				</template>
				<span>Nuevo Porcentaje de Patrocinio</span>
			</v-tooltip>
		</div>
		<v-divider class="my-2"></v-divider>
		<v-list class="pt-0" style="max-height: 300px;overflow: scroll;" v-if="percents.length > 0">
			<v-list-item
				v-for="(item, i) in percents"
				:key="i"
				class="px-0 py-0"
			>
				<div style="width: 100%">
					<div class="d-flex flex-row px-4 justify-space-between align-center" style="gap: 15px;">
						<div class="d-flex flex-1 align-center justify-space-between">
							<div class="px-4 caption">
								<p class="mb-0 caption font-weight-bold" style="color: #707070;">Patrocinio {{ item.percent }}%</p>
							</div>
						</div>
						<v-divider class="mx-2" vertical></v-divider>
						<div>
							<v-tooltip bottom>
								<template v-slot:activator="{ on, attrs }">
									<v-btn
										icon
										dark
										x-small
										color="warning"
										v-if="$checkPermissions(['edit_company_supervision_percents'])"
										@click="openModal(item)"
										v-bind="attrs"
										v-on="on"
									>
										<v-icon small dark>mdi-pencil</v-icon>
									</v-btn>
								</template>
								<span>Editar Porcentaje de Patrocinio</span>
							</v-tooltip>
							<v-tooltip bottom>
								<template v-slot:activator="{ on, attrs }">
									<v-btn
										icon
										dark
										x-small
										color="error"
										v-if="$checkPermissions(['delete_company_supervision_percents'])"
										@click="deletePercent(item)"
										v-bind="attrs"
										v-on="on"
									>
										<v-icon small dark>mdi-delete-outline</v-icon>
									</v-btn>
								</template>
								<span>Eliminar Porcentaje de Patrocinio</span>
							</v-tooltip>
						</div>
					</div>
				</div>
			</v-list-item>
		</v-list>
		<div v-else class="px-4 py-3">
			<p class="mb-0 caption" style="color: #999999;">No hay porcentajes de patrocinio registrados</p>
		</div>

		<!-- Modal para crear/editar -->
		<v-dialog v-model="dialog" max-width="400px">
			<v-card>
				<v-card-title style="background-color: #524D4D; color: white;">
					{{ editingPercent ? 'Editar Porcentaje de Patrocinio' : 'Nuevo Porcentaje de Patrocinio' }}
				</v-card-title>
				<v-card-text class="pt-4">
					<v-form ref="form" v-model="validForm">
						<v-text-field
							v-model="formData.percent"
							type="number"
							label="Porcentaje (%)"
							outlined
							required
							dense
							hide-details
							class="custom-height-search custom-height-calendar mt-3"
							style="font-size: 14px;"
							:rules="percentRules"
							@input="sanitizePercent"
						/>
					</v-form>
				</v-card-text>
				<v-card-actions>
					<div style="width: 100%;">
						<v-divider class="my-2"></v-divider>
						<div class="d-flex justify-end align-center" style="gap:10px;">
							<v-btn
								class="mx-2 my-1"
								color="white"
								raised
								@click="dialog = false"
							>
								Cancelar
							</v-btn>
							<v-btn
								class="mx-2 my-1"
								color="#524D4D"
								raised
								dark
								:loading="saving"
								@click="savePercent"
							>
								Confirmar
							</v-btn>
						</div>
					</div>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</div>
</template>

<script>
import { eventBus } from '@/components/Helpers/Events/NotificationsEventBus'
import { getCompanySupervisionPercents, createCompanySupervisionPercent, updateCompanySupervisionPercent, deleteCompanySupervisionPercent } from '@/helpers/api/costs'

export default {
	name: 'CompanySupervisionPercentConfig',
	props: {
		company: {
			type: Object,
			required: true
		}
	},
	data() {
		return {
			percents: [],
			dialog: false,
			saving: false,
			validForm: true,
			editingPercent: null,
			formData: {
				percent: '',
				company_supervision_percent_id: null,
				company_id: null
			},
			percentRules: [
				v => !!v || 'El porcentaje es requerido',
				v => (v >= 0 && v <= 100) || 'El porcentaje debe ser entre 0 y 100'
			],
			loadingPercents: false
		}
	},
	computed: {
		selectedCompany() {
			return this.$store.state.companies.selectedCompany
		}
	},
	watch: {
		selectedCompany(newValue) {
			if (newValue) {
				this.fetchPercents()
			}
		}
	},
	mounted() {
		if (this.selectedCompany) {
			this.fetchPercents()
		}
	},
	methods: {
		async fetchPercents() {
			if (!this.selectedCompany) return
			
			try {
				this.loadingPercents = true
				const response = await getCompanySupervisionPercents({
					company_id: this.company?.id || this.selectedCompany.id
				})
				if (response.code === 200) {
					this.percents = response.percents || []
				} else {
					this.percents = []
				}
			} catch (error) {
				console.error('Error al obtener porcentajes de patrocinio:', error)
				this.percents = []
			} finally {
				this.loadingPercents = false
			}
		},
		openModal(percent) {
			this.editingPercent = percent
			if (percent) {
				this.formData = {
					percent: percent.percent,
					company_supervision_percent_id: percent.id,
					company_id: this.selectedCompany.id
				}
			} else {
				this.formData = {
					percent: '',
					company_supervision_percent_id: null,
					company_id: this.selectedCompany.id
				}
			}
			this.dialog = true
		},
		async savePercent() {
			if (!this.$refs.form.validate()) return

			this.saving = true
			try {
				if (this.editingPercent) {
					// Editar
					await updateCompanySupervisionPercent({
						company_supervision_percent_id: this.formData.company_supervision_percent_id,
						percent: this.formData.percent
					})
				} else {
					// Crear
					await createCompanySupervisionPercent({
						percent: this.formData.percent,
						company_id: this.selectedCompany.id
					})
				}
				this.dialog = false
				this.$refs.form.reset()
				eventBus.$emit('company_supervision_percents_updated')
				await this.fetchPercents()
			} catch (error) {
				console.error('Error al guardar porcentaje de patrocinio:', error)
			} finally {
				this.saving = false
			}
		},
		async deletePercent(percent) {
			if (!confirm(`¿Estás seguro de que deseas eliminar el porcentaje de patrocinio ${percent.percent}%?`)) {
				return
			}

			try {
				await deleteCompanySupervisionPercent({
					company_supervision_percent_id: percent.id
				})
				eventBus.$emit('company_supervision_percents_updated')
				await this.fetchPercents()
			} catch (error) {
				console.error('Error al eliminar porcentaje de patrocinio:', error)
			}
		},
		sanitizePercent() {
			let value = String(this.formData.percent)
			value = value.replace(/[^0-9]/g, '')
			const numValue = parseInt(value) || 0
			this.formData.percent = Math.min(Math.max(numValue, 0), 100)
		}
	}
}
</script>

<style scoped>
</style>
